"use client";

import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import {
  Button,
  Input,
  Skeleton,
  SkeletonTable,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Tooltip,
  cn,
} from "@chotca/ui";

export type Column<T> = {
  key: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  className?: string;
  align?: "left" | "right" | "center";
  width?: string;
};

export type RowAction<T> = {
  key: string;
  /** Tooltip label (also used as aria-label). */
  label: string;
  icon: LucideIcon;
  onClick: (row: T) => void;
  show?: (row: T) => boolean;
  destructive?: boolean;
  disabled?: (row: T) => boolean;
  loading?: (row: T) => boolean;
};

export type BulkAction<T> = {
  key: string;
  label: string;
  icon?: LucideIcon;
  onClick: (rows: T[]) => void;
  destructive?: boolean;
  disabled?: (rows: T[]) => boolean;
  loading?: (rows: T[]) => boolean;
};

export type DataTableProps<T> = {
  columns: Column<T>[];
  data: T[];
  /** First load (no rows yet) — shows the full skeleton. */
  isLoading?: boolean;
  /**
   * Background refetch while rows are already on screen — e.g. paging or
   * applying a filter. Keeps the current rows in place under a dimmed overlay
   * so the layout (and the pagination footer) stays put. Pass React Query's
   * `isFetching` here.
   */
  isFetching?: boolean;
  rowKey: (row: T) => string;
  // Search
  search?: string;
  onSearchChange?: (v: string) => void;
  searchPlaceholder?: string;
  // Header (right side: filters, create, etc.)
  headerActions?: React.ReactNode;
  // Per-row inline actions (icon-only, hover-revealed, overlay on right)
  rowActions?: RowAction<T>[];
  // Bulk select
  bulkActions?: BulkAction<T>[];
  // Page-number pagination. Provide `total` + `onPageChange` to show the footer.
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  // Empty state
  emptyText?: string;
  emptyDescription?: string;
  // Optional click on row (e.g., view detail)
  onRowClick?: (row: T) => void;
  // Optional touch-friendly card renderer. When provided, the table is hidden
  // on small screens (<md) and this card list is shown instead — columns in a
  // horizontally-scrolling table read poorly on phones, and the table's
  // hover-revealed row actions are unreachable by touch. Loading skeleton,
  // empty state, and pagination are still handled by DataTable.
  renderMobileCard?: (row: T) => React.ReactNode;
};

function EmptyTableIllustration() {
  return (
    <svg
      width="140"
      height="116"
      viewBox="0 0 140 116"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Soft backdrop ellipse */}
      <ellipse cx="70" cy="68" rx="56" ry="44" fill="#E8E8E4" opacity="0.55" />

      {/* Table drop shadow */}
      <rect
        x="27"
        y="33"
        width="90"
        height="68"
        rx="10"
        fill="#D4D4CE"
        opacity="0.25"
      />

      {/* Table card */}
      <rect
        x="24"
        y="29"
        width="90"
        height="68"
        rx="10"
        fill="white"
        stroke="#E0E0DA"
        strokeWidth="1.5"
      />

      {/* Header row */}
      <rect x="24" y="29" width="90" height="23" rx="10" fill="#F5F5F2" />
      <rect x="24" y="41" width="90" height="11" fill="#F5F5F2" />

      {/* Header column separators */}
      <line x1="64" y1="29" x2="64" y2="52" stroke="#E0E0DA" strokeWidth="1" />
      <line x1="97" y1="29" x2="97" y2="52" stroke="#E0E0DA" strokeWidth="1" />

      {/* Header bottom divider */}
      <line
        x1="24"
        y1="52"
        x2="114"
        y2="52"
        stroke="#E0E0DA"
        strokeWidth="1.5"
      />

      {/* Header label pills */}
      <rect x="34" y="38" width="22" height="4" rx="2" fill="#C8C8C2" />
      <rect x="73" y="38" width="16" height="4" rx="2" fill="#C8C8C2" />
      <rect x="104" y="38" width="7" height="4" rx="2" fill="#C8C8C2" />

      {/* Empty row 1 */}
      <rect x="34" y="61" width="30" height="4" rx="2" fill="#E8E8E4" />
      <rect
        x="73"
        y="61"
        width="20"
        height="4"
        rx="2"
        fill="#E8E8E4"
        opacity="0.7"
      />
      <rect
        x="104"
        y="61"
        width="7"
        height="4"
        rx="2"
        fill="#E8E8E4"
        opacity="0.5"
      />

      {/* Row 1 divider */}
      <line x1="34" y1="72" x2="107" y2="72" stroke="#F0F0EC" strokeWidth="1" />

      {/* Empty row 2 */}
      <rect
        x="34"
        y="77"
        width="22"
        height="4"
        rx="2"
        fill="#E8E8E4"
        opacity="0.55"
      />
      <rect
        x="73"
        y="77"
        width="28"
        height="4"
        rx="2"
        fill="#E8E8E4"
        opacity="0.4"
      />
      <rect
        x="104"
        y="77"
        width="7"
        height="4"
        rx="2"
        fill="#E8E8E4"
        opacity="0.3"
      />

      {/* Row 2 divider */}
      <line x1="34" y1="88" x2="107" y2="88" stroke="#F0F0EC" strokeWidth="1" />

      {/* Empty row 3 */}
      <rect
        x="34"
        y="92"
        width="16"
        height="4"
        rx="2"
        fill="#E8E8E4"
        opacity="0.3"
      />
      <rect
        x="73"
        y="92"
        width="12"
        height="4"
        rx="2"
        fill="#E8E8E4"
        opacity="0.2"
      />

      {/* Floating magnifying glass */}
      <circle
        cx="106"
        cy="25"
        r="13"
        fill="white"
        stroke="#E0E0DA"
        strokeWidth="1.5"
      />
      <circle cx="105" cy="24" r="5" stroke="#C8C8C2" strokeWidth="1.5" />
      <line
        x1="109"
        y1="28"
        x2="113"
        y2="32"
        stroke="#C8C8C2"
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      {/* Lime accent dots */}
      <circle cx="31" cy="24" r="4" fill="#32ffaa" opacity="0.55" />
      <circle cx="40" cy="17" r="2.5" fill="#32ffaa" opacity="0.28" />
      <circle cx="24" cy="16" r="1.5" fill="#32ffaa" opacity="0.18" />
    </svg>
  );
}

/** Stacked card placeholders shown while a mobile card list loads. */
function MobileCardsSkeleton() {
  return (
    <div className="space-y-2.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-2xl border-[1.5px] border-smoke bg-paper p-3.5"
        >
          <Skeleton className="h-11 w-11 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton
              className="h-3.5 w-2/5 rounded-md"
              style={
                { "--shimmer-delay": `${i * 70}ms` } as React.CSSProperties
              }
            />
            <Skeleton
              className="h-3 w-3/5 rounded-md"
              style={
                { "--shimmer-delay": `${i * 70 + 35}ms` } as React.CSSProperties
              }
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Empty state for the mobile card list (mirrors the desktop table empty). */
function MobileEmpty({
  text,
  description,
}: {
  text: string;
  description?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl border-[1.5px] border-smoke bg-paper py-14 text-center">
      <EmptyTableIllustration />
      <p className="text-sm font-medium text-ash">{text}</p>
      {description ? (
        <p className="max-w-xs px-6 text-xs text-ash/70">{description}</p>
      ) : null}
    </div>
  );
}

/**
 * Dimmed wash + centered spinner laid over the still-visible rows during a
 * refetch (page change / filter). Keeps the table at its current height so the
 * pagination footer doesn't jump.
 */
function LoadingOverlay() {
  return (
    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-paper/55 backdrop-blur-[1px]">
      <span className="sticky top-1/2 inline-flex h-7 w-7 animate-spin rounded-full border-2 border-ink/25 border-t-ink" />
    </div>
  );
}

function IconButton({
  icon: Icon,
  label,
  onClick,
  destructive,
  disabled,
  loading,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  loading?: boolean;
}) {
  return (
    <Tooltip label={label}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        disabled={disabled || loading}
        className={cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors",
          "disabled:cursor-not-allowed disabled:opacity-50",
          destructive
            ? "text-rose-700 hover:bg-rose-100 active:bg-rose-200"
            : "text-ink hover:bg-mist active:bg-smoke",
        )}
      >
        {loading ? (
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <Icon size={16} strokeWidth={1.75} />
        )}
      </button>
    </Tooltip>
  );
}

function Checkbox({
  checked,
  indeterminate,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}) {
  const ref = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      onClick={(e) => e.stopPropagation()}
      aria-label={ariaLabel}
      className="h-4 w-4 cursor-pointer rounded border-smoke text-ink accent-ink"
    />
  );
}

export function DataTable<T>({
  columns,
  data,
  isLoading,
  isFetching,
  rowKey,
  search,
  onSearchChange,
  searchPlaceholder = "Tìm kiếm...",
  headerActions,
  rowActions,
  bulkActions,
  page = 1,
  pageSize = 20,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [20, 50, 100],
  emptyText = "Không có dữ liệu",
  emptyDescription,
  onRowClick,
  renderMobileCard,
}: DataTableProps<T>) {
  const hasRowActions = (rowActions?.length ?? 0) > 0;
  const hasBulk = (bulkActions?.length ?? 0) > 0;
  const hasPager = total !== undefined && !!onPageChange;

  // Show the full skeleton only on the *first* load (no rows yet). When data is
  // already on screen — e.g. paging or applying a filter — keep the current
  // rows visible under a dimmed overlay instead of collapsing to a short
  // skeleton, which would yank the pagination footer up and down ("giật").
  const hasData = data.length > 0;
  const busy = !!isLoading || !!isFetching;
  const showInitialSkeleton = busy && !hasData;
  const isRefetching = busy && hasData;

  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  // Reset selection when data set fundamentally changes (length 0 etc.)
  React.useEffect(() => {
    setSelected((prev) => {
      const valid = new Set(data.map(rowKey));
      const next = new Set<string>();
      for (const id of prev) if (valid.has(id)) next.add(id);
      return next;
    });
  }, [data, rowKey]);

  const allKeys = React.useMemo(() => data.map(rowKey), [data, rowKey]);
  const allSelected =
    allKeys.length > 0 && allKeys.every((k) => selected.has(k));
  const someSelected = allKeys.some((k) => selected.has(k)) && !allSelected;

  function toggleAll(next: boolean) {
    setSelected(next ? new Set(allKeys) : new Set());
  }
  function toggleOne(key: string, next: boolean) {
    setSelected((prev) => {
      const n = new Set(prev);
      if (next) n.add(key);
      else n.delete(key);
      return n;
    });
  }
  function clearSelection() {
    setSelected(new Set());
  }

  const selectedRows = data.filter((r) => selected.has(rowKey(r)));

  return (
    <div className="space-y-3">
      {/* Toolbar (search + header actions) */}
      {(onSearchChange || headerActions) && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {onSearchChange ? (
            <div className="relative sm:w-80">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ash"
                size={16}
              />
              <Input
                value={search ?? ""}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-9"
              />
            </div>
          ) : (
            <div />
          )}
          {headerActions ? (
            <div className="flex flex-wrap items-center gap-2">
              {headerActions}
            </div>
          ) : null}
        </div>
      )}

      {/* Bulk-selection toolbar (replaces nothing; appears above the table) */}
      {hasBulk && selectedRows.length > 0 ? (
        <div className="flex items-center justify-between rounded-2xl border border-ink/15 bg-ink/5 px-4 py-2.5">
          <div className="flex items-center gap-3 text-sm">
            <button
              type="button"
              className="text-xs font-semibold text-ash hover:text-ink"
              onClick={clearSelection}
            >
              Bỏ chọn
            </button>
            <span className="font-semibold text-ink">
              {selectedRows.length} đã chọn
            </span>
          </div>
          <div className="flex items-center gap-1">
            {bulkActions!.map((a) => (
              <Button
                key={a.key}
                size="sm"
                variant={a.destructive ? "ghost" : "primary"}
                onClick={() => a.onClick(selectedRows)}
                disabled={a.disabled?.(selectedRows)}
                loading={a.loading?.(selectedRows)}
                className={
                  a.destructive ? "text-rose-700 hover:bg-rose-100" : undefined
                }
              >
                {a.icon ? (
                  <a.icon size={14} className="mr-1" strokeWidth={1.75} />
                ) : null}
                {a.label}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      <div className={renderMobileCard ? "hidden md:block" : undefined}>
        {showInitialSkeleton ? (
          <SkeletonTable rows={6} cols={columns.length + (hasBulk ? 1 : 0)} />
        ) : (
          <div
            className={cn("relative", isRefetching && "transition-opacity")}
            aria-busy={isRefetching}
          >
            <Table>
              <THead>
                <TR>
                  {hasBulk ? (
                    <TH style={{ width: 40 }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={toggleAll}
                        ariaLabel="Chọn tất cả"
                      />
                    </TH>
                  ) : null}
                  {columns.map((c) => (
                    <TH
                      key={c.key}
                      className={cn(
                        c.className,
                        c.align === "right" && "text-right",
                        c.align === "center" && "text-center",
                      )}
                      style={c.width ? { width: c.width } : undefined}
                    >
                      {c.header}
                    </TH>
                  ))}
                  {/* No header label for the actions column — it's hover-revealed. */}
                  {hasRowActions ? <TH style={{ width: 1 }} /> : null}
                </TR>
              </THead>
              <TBody>
                {data.length === 0 ? (
                  <TR>
                    <TD
                      colSpan={
                        columns.length +
                        (hasRowActions ? 1 : 0) +
                        (hasBulk ? 1 : 0)
                      }
                      className="py-14 text-center"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <EmptyTableIllustration />
                        <p className="text-sm font-medium text-ash">
                          {emptyText}
                        </p>
                        {emptyDescription ? (
                          <p className="max-w-xs text-xs text-ash/70">
                            {emptyDescription}
                          </p>
                        ) : null}
                      </div>
                    </TD>
                  </TR>
                ) : (
                  data.map((row) => {
                    const key = rowKey(row);
                    const isSelected = selected.has(key);
                    return (
                      <TR
                        key={key}
                        className={cn(
                          "group relative",
                          onRowClick && "cursor-pointer",
                          isSelected && "bg-ink/4",
                        )}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                      >
                        {hasBulk ? (
                          <TD
                            className="w-10"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={isSelected}
                              onChange={(next) => toggleOne(key, next)}
                              ariaLabel="Chọn dòng"
                            />
                          </TD>
                        ) : null}
                        {columns.map((c) => (
                          <TD
                            key={c.key}
                            className={cn(
                              c.className,
                              c.align === "right" && "text-right",
                              c.align === "center" && "text-center",
                            )}
                          >
                            {c.cell(row)}
                          </TD>
                        ))}
                        {hasRowActions ? (
                          <TD
                            className="relative p-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Overlay container — anchored to right of row, hidden until row hover */}
                            <div
                              className={cn(
                                "pointer-events-none absolute right-2 top-1/2 z-10 -translate-y-1/2",
                                "flex items-center gap-0.5 rounded-full bg-paper/95 pl-2 pr-1.5 shadow-md ring-1 ring-smoke backdrop-blur",
                                "opacity-0 transition-opacity duration-100",
                                "group-hover:pointer-events-auto group-hover:opacity-100",
                                "focus-within:pointer-events-auto focus-within:opacity-100",
                              )}
                            >
                              {rowActions!
                                .filter((a) => (a.show ? a.show(row) : true))
                                .map((a) => (
                                  <IconButton
                                    key={a.key}
                                    icon={a.icon}
                                    label={a.label}
                                    destructive={a.destructive}
                                    disabled={a.disabled?.(row)}
                                    loading={a.loading?.(row)}
                                    onClick={() => a.onClick(row)}
                                  />
                                ))}
                            </div>
                          </TD>
                        ) : null}
                      </TR>
                    );
                  })
                )}
              </TBody>
            </Table>
            {isRefetching ? <LoadingOverlay /> : null}
          </div>
        )}
      </div>

      {renderMobileCard ? (
        <div className="md:hidden">
          {showInitialSkeleton ? (
            <MobileCardsSkeleton />
          ) : data.length === 0 ? (
            <MobileEmpty text={emptyText} description={emptyDescription} />
          ) : (
            <div className="relative" aria-busy={isRefetching}>
              <div className="space-y-2.5">
                {data.map((row) => (
                  <div key={rowKey(row)}>{renderMobileCard(row)}</div>
                ))}
              </div>
              {isRefetching ? <LoadingOverlay /> : null}
            </div>
          )}
        </div>
      ) : null}

      {hasPager ? (
        <Pager
          page={page}
          pageSize={pageSize}
          total={total!}
          pageSizeOptions={pageSizeOptions}
          onPageChange={onPageChange!}
          onPageSizeChange={onPageSizeChange}
        />
      ) : null}
    </div>
  );
}

/** Builds the page-button sequence with ellipsis gaps (e.g. 1 … 4 5 6 … 12). */
function pageItems(current: number, totalPages: number): (number | "…")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const items: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);
  if (start > 2) items.push("…");
  for (let p = start; p <= end; p++) items.push(p);
  if (end < totalPages - 1) items.push("…");
  items.push(totalPages);
  return items;
}

function Pager({
  page,
  pageSize,
  total,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(page, 1), totalPages);
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(current * pageSize, total);

  return (
    <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3 text-xs text-ash">
        <span>
          {from} - {to} trong {total}
        </span>
        {onPageSizeChange ? (
          <label className="flex items-center gap-1.5">
            <span>Hiển thị</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-md border border-smoke bg-paper px-1.5 py-1 text-xs font-medium text-ink focus:border-ink focus:outline-none"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span>/ trang</span>
          </label>
        ) : null}
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          disabled={current <= 1}
          onClick={() => onPageChange(current - 1)}
          aria-label="Trang trước"
        >
          <ChevronLeft size={16} />
        </Button>
        {pageItems(current, totalPages).map((it, i) =>
          it === "…" ? (
            <span
              key={`gap-${i}`}
              className="px-1.5 text-xs text-ash"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <button
              key={it}
              type="button"
              onClick={() => onPageChange(it)}
              aria-current={it === current ? "page" : undefined}
              className={cn(
                "inline-flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-xs font-semibold transition-colors",
                it === current
                  ? "bg-ink text-paper"
                  : "text-ink hover:bg-mist active:bg-smoke",
              )}
            >
              {it}
            </button>
          ),
        )}
        <Button
          variant="ghost"
          size="sm"
          disabled={current >= totalPages}
          onClick={() => onPageChange(current + 1)}
          aria-label="Trang sau"
        >
          <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
}

/**
 * Page-number pagination state for web tables. `page` is 1-based; changing
 * the page size resets to page 1 so the offset stays valid.
 */
export function usePagination(initialPageSize = 20) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(initialPageSize);
  function changePageSize(size: number) {
    setPageSize(size);
    setPage(1);
  }
  function reset() {
    setPage(1);
  }
  return { page, pageSize, setPage, setPageSize: changePageSize, reset };
}
